'use client';

import useAuthGuard from '@/hooks/auth/useAuthGuard';
import { StytchFactorOtp } from '../stytchFactorOtp';
import { useSession } from '@/contexts/LitSessionProvider';
import ErrorScreen from '../../errors/ErrorScreen';
import useOnlineStatus from '@rehooks/online-status';
import DesktopOnboarding from './DesktopOnboarding';
import DesktopProfileForm from '../../userSettings/dektop/DesktopProfileForm';
import DesktopDeposit from './DesktopDeposit';
import CustomDialog from '@/components/ui/customDialog';
import { Button } from '@/components/ui/button';
import useGetUserProfileInfo from '@/hooks/auth/useGetUserProfileInfo';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import DesktopLoader from '@/components/shared/DesktopLoader';
import { useRouter } from 'next/router';
import OfflineErrorScreen from '@/components/errors/OfflineErrorScreen';
import LoggedInUserMenu from './LoggedInUserMenu';
import Loading from '@/components/shared/Loading';
import ReferralCodeInput from '../ReferralCodeInput';
import { useLocalStorage } from 'usehooks-ts';
import FeedPeople from '@/components/explore/desktop/people/FeedPeople';
export default function DesktopGuard({
  TwoFa = true,
  triggerView = true,
  children
}: {
  TwoFa?: boolean;
  triggerView?: boolean;
  children?: any;
}) {
  const {
    showAuthLoader,
    shouldOnboard,
    showReferralInput,
    showTwoFaModal,
    showDeposit,
    runAuthFlow,
    setMFAInfo,
    MFAInfo,
    handleUseReferralCode,
    referralUrlCode,
    hasUsername,
    cloudData,
    progressBar,
    mfaLoading,
    showBalancesLoader
  } = useAuthGuard(TwoFa);
  const {
    PKP,
    decryptedWallets,
    clients,
    clearLitSession
  } = useSession();
  const [disableRef, setDisableRef] = useLocalStorage(`disableRef-${PKP?.ethAddress}`, false);
  const {
    userName,
    PFP
  } = useGetUserProfileInfo();
  const onlineStatus = useOnlineStatus();
  const [showLoader, setShowLoader] = useState(false); //It is to optimize SEO
  const [isCLient, setIsCLient] = useState(false);
  useEffect(() => {
    setIsCLient(true);
    const handleMouseMove = () => {
      setShowLoader(true);
      window.removeEventListener('mousemove', handleMouseMove);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  function renderTrigger(buttonTitle: string) {
    if (triggerView) {
      return buttonTitle === 'Deposit' ? <div>
          <Button variant='pink' className='h-[56px] w-full rounded-full' aria-label=' Deposit Cash or Crypto'>
            Deposit Cash or Crypto
          </Button>
        </div> : <div className='w-full referalBanner p-2 rounded-[20px] justify-center h-[455px] ta-c cursor-pointer genius-shadow border-genius-blue border-[1px]'>
          <Image src='/static/genius.png' width={100} height={100} className='w-[36px] h-[36px] mt-[76px]' alt='logo' />
          <p className='text-[16px] text-genius-gradient mt-[56px] tracking-tighter'>
            Welcome{userName ? ', ' + userName.replace('@', '') : ' Genius.'}
          </p>

          <p className='text-[24px] text-genius-gradient mt-[36px] tracking-tighter'>
            Get started, here
          </p>
          <div className='h-[170px] flex items-end w-full'>
            <Button variant='pink' className='h-[56px] w-full rounded-full' aria-label={buttonTitle}>
              {buttonTitle}
            </Button>
          </div>
        </div>;
    } else return <div className='flex items-center justify-center w-full'>
          <Button className='w-fit h-[32px] rounded-full bg-genius-pink hover:bg-genius-pink text-[16px] text-genius-blue' variant='ghost' aria-label='Sign Up'>
            Sign Up
          </Button>
        </div>;
  }
  if (!onlineStatus && triggerView) {
    return <>
        <OfflineErrorScreen />;
      </>;
  }
  if (!PKP && clients?.clientCreationError && triggerView) {
    return <>
        {' '}
        <ErrorScreen />
      </>;
  }
  const router = useRouter();
  const onPortfolioPage = router.pathname?.includes('portfolio');
  const onFeedPage = router.pathname?.includes('feed');
  const deletePage = router.pathname?.includes('delete');
  if (!isCLient) {
    return;
  }
  // Show onboarding layout
  if (showLoader && showAuthLoader || mfaLoading || showBalancesLoader || progressBar) {
    if (PKP?.ethAddress && decryptedWallets?.length && !mfaLoading) {
      if (onPortfolioPage) {
        return <div className=''>{triggerView ? <DesktopLoader /> : <>Authenticating...</>}</div>;
      }
      return <>
          {triggerView ? <div className='w-full flex items-center justify-center
             genius-shadow  bg-genius-indigo rounded-[20px] border-genius-blue hover:bg-genius-blue border-[1px] overflow-hidden h-[455px] 
            
             '>
              <Loading />
            </div> : <> </>}
        </>;
    } else {
      return <>
          {triggerView ? <div className='w-full  h-[455px] '>
              <div className='w-full h-full relative'>
                <DesktopLoader />
                {progressBar ? <div className='absolute bottom-[4%] left-0 w-full flex  justify-center items-center'>
                    <div className='w-1/2 rounded-full  mx-auto h-1 bg-transparent border-genius-blue border-[1px]'>
                      <div className='h-1 bg-genius-pink rounded-full ' style={{
                  width: `${progressBar}%`
                }}></div>
                    </div>
                  </div> : <> </>}
              </div>
            </div> : <></>}
        </>;
    }
  }
  if (shouldOnboard) {
    return <>
        <CustomDialog className={'h-fit w-fit'} trigger={renderTrigger('Create Account / Sign In')}>
          <DesktopOnboarding runAuthFlow={runAuthFlow} />
        </CustomDialog>
      </>;
  }

  // Show referral code input
  if (showReferralInput && !disableRef) {
    return <CustomDialog isOpen={true} hideClose={true} className={'h-fit w-[572px]'} trigger={renderTrigger('Enter Referral Code')}>
        <ReferralCodeInput handleUseReferralCode={handleUseReferralCode} referralUrlCode={referralUrlCode} clearLitSession={clearLitSession} />
      </CustomDialog>;
  }

  // profile not configured
  if (!hasUsername) {
    return <CustomDialog isOpen={true} hideClose={true} className={'w-[572px] justify-center items-center overflow-y-auto no-scrollbar '} trigger={renderTrigger('Set User Profile')}>
        <DesktopProfileForm userInfo={cloudData} />
      </CustomDialog>;
  }
  const MFAIsEmail = MFAInfo?.presentMFAInfo?.authMethodType == 10;
  const MFAIsSMS = MFAInfo?.presentMFAInfo?.authMethodType == 11;

  // Show two-factor authentication modal
  if (showTwoFaModal) {
    return <>
        <CustomDialog className={'h-fit w-[572px] justify-center'} isOpen={true} hideClose={true} trigger={renderTrigger('Confirm 2FA')}>
          <div className='my-4 text-sm px-6 text-genius-cream/50'>
            {' '}
            You have Two Factor Authentication enabled via{' '}
            {MFAIsEmail ? 'email' : MFAIsSMS ? 'SMS' : 'Whatsapp'} . Please{' '}
            {MFAIsEmail ? 'check the email address' : 'check the phone number'} you used to enable
            Two Factor Auth to receive a code.
          </div>
          <StytchFactorOtp authMethodType={MFAInfo?.presentMFAInfo?.authMethodType} askingAuthId={MFAInfo?.presentMFAInfo?.authId} askingProviderValue={MFAInfo?.presentMFAInfo?.providerValue} disableInput={true} skipFinalConfirmationScreen={true} callBack={async (authMethod, providerValue) => {
          setMFAInfo(prev => ({
            ...prev,
            authMethod,
            providerValue
          }));
          return {
            success: true
          };
        }} />
        </CustomDialog>
      </>;
  }

  // Show deposit screen
  if (showDeposit && children) {
    return <>
        <CustomDialog className={'h-fit w-[572px]'} trigger={renderTrigger('Deposit')}>
          <DesktopDeposit />
        </CustomDialog>
        <FeedPeople className='h-[81vh]' />
      </>;
  }
  if (children) return children;

  //If there is not predefine triggerView and user is loggedin we show the user menu
  if (!triggerView) {
    return <LoggedInUserMenu PFP={PFP} clearLitSession={clearLitSession} />;
  }
}